import React from "react";
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Mail, Phone, Linkedin, Copy } from "lucide-react";



interface TeamMember {
    fullname: string;
    position: string;
    office: string;
    email?: string;
    mobile?: string;
    linkedinurl?: string;
    photourl?: string;
    bio?: string;
}

interface TeamMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedItem: TeamMember | null;
    decodeHtmlAndUrl: (input: string) => string;
}

const TeamMemberDialog: React.FC<TeamMemberDialogProps> = ({ open, onOpenChange, selectedItem, decodeHtmlAndUrl }) => {
    if (!selectedItem) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md w-full h-screen md:h-auto p-0 md:rounded-2xl shadow-2xl border border-gray-200 bg-white text-gray-900 relative overflow-hidden">
                {/* Decorative background */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-10 -left-10 w-32 h-32 bg-harvey-red/10 rounded-full blur-2xl animate-pulse"></div>
                    <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-harvey-blue/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
                    <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-harvey-orange/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2 animate-pulse delay-500"></div>
                </div>
                <button
                    onClick={() => {
                        onOpenChange(false);
                    }}
                    className="absolute top-4 right-4 z-20 bg-white/80 hover:bg-gray-100 rounded-full p-2 shadow transition-colors focus:outline-none border-none"
                    aria-label="Close dialog"
                >
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                {/* Card Content */}
                <div className="flex flex-col items-center px-8 py-10 relative z-10">
                    {/* Profile Image with animated ring */}
                    <div className="relative mb-4">
                        <div className="absolute inset-0 animate-spin-slow rounded-full border-4 border-dashed border-harvey-red/30"></div>
                        <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-harvey-red shadow-lg bg-gray-100 flex items-center justify-center">
                            {selectedItem.photourl ? (
                                <img
                                    src={selectedItem.photourl}
                                    alt={selectedItem.fullname}
                                    className="object-cover w-full h-full"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-3xl text-gray-400 bg-gray-200">
                                    {selectedItem.fullname.charAt(0)}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Name & Position */}
                    <h2 className="text-2xl font-bold text-harvey-red mb-1 text-center flex items-center gap-2">
                        {selectedItem.fullname}
                        <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse" title="Active"></span>
                    </h2>
                    <div className="text-base font-medium text-gray-700 mb-1 text-center flex items-center gap-2">
                        {selectedItem.position}
                        <span className="text-xs text-harvey-blue bg-harvey-blue/10 px-2 py-0.5 rounded-full ml-1">{selectedItem.office}</span>
                    </div>
                    {/* Bio */}
                    {selectedItem.bio && (
                        <div
                            className="text-gray-700 text-sm leading-relaxed mb-4 text-center mt-2 px-2"
                            dangerouslySetInnerHTML={{ __html: decodeHtmlAndUrl(selectedItem.bio) }}
                        />
                    )}
                    {/* Contact Buttons */}
                    <div className="flex flex-col gap-2 w-full mt-2">
                        {selectedItem.email && (
                            <>
                                {/* Mobile: simple mailto button */}
                                <a
                                    href={`mailto:${selectedItem.email}`}
                                    className="btn md:hidden flex items-center justify-center gap-2 w-full py-2 px-4 rounded-lg bg-harvey-red text-white font-semibold hover:bg-harvey-red/90 transition-colors text-base shadow-md hover:scale-[1.03] active:scale-95"
                                >
                                    <Mail className="w-5 h-5" />
                                    {selectedItem.email}
                                </a>

                                {/* Desktop: email display with copy action (hidden on mobile) */}
                                <div className="hidden md:flex items-center justify-between md:items-center  w-full py-2 px-3 rounded-lg bg-harvey-red text-white font-semibold shadow-md">
                                    <a
                                        href={`mailto:${selectedItem.email}`}
                                        title={`Send email to ${selectedItem.email}`}
                                        aria-label={`Send email to ${selectedItem.email}`}
                                        className="btn flex items-center  md:items-center gap-12  w-full hover:bg-harvey-red/90 transition-colors text-base  hover:scale-[1.03] active:scale-95">
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <span className="select-all text-sm md:text-base break-words">{selectedItem.email}</span>
                                        </div>
                                    </a>

                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    await navigator.clipboard.writeText(selectedItem.email || "");
                                                    // minimal visual feedback: console log (replace with toast if available)
                                                    console.debug("Email copied to clipboard");
                                                } catch (e) {
                                                    console.warn("Copy failed", e);
                                                }
                                            }}
                                            className="btn inline-flex items-center justify-center gap-1 p-2 rounded bg-transparent hover:bg-white/20 transition"
                                            title="Copy email address"
                                            aria-label="Copy email address"
                                        >
                                            <Copy className="w-4 h-4 text-white" />
                                        </button>
                                    </div>

                                </div>
                            </>
                        )}


                        {selectedItem.mobile && (
                            <>
                                {/* Clickable on small screens only */}
                                <a
                                    href={`tel:${selectedItem.mobile}`}
                                    className="btn md:hidden flex items-center justify-center gap-2 w-full py-2 px-4 rounded-lg bg-harvey-blue text-white font-semibold hover:bg-harvey-blue/90 transition-colors text-base shadow-md hover:scale-[1.03] active:scale-95"
                                >
                                    <Phone className="w-5 h-5" />
                                    {selectedItem.mobile}
                                </a>

                                {/* Non-clickable on medium+ screens */}
                                <div className="hidden md:flex items-center justify-between md:items-center gap-2 w-full py-2 px-3 rounded-lg bg-harvey-blue/90 text-white font-semibold transition-colors text-base shadow-md">
                                    <div className="flex items-center gap-2">
                                        <Phone className="w-5 h-5" />

                                    </div>
                                    <div>
                                        <span className="select-all">{selectedItem.mobile}</span>

                                    </div>

                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(selectedItem.mobile || "");
                                                // optional visual feedback: small toast/alert — keep minimal here
                                                // alert("Phone number copied");
                                            } catch (e) {
                                                // fallback
                                                console.warn("Copy failed", e);
                                            }
                                        }}
                                        className="btn hidden md:inline-flex items-center justify-center gap-1 p-2 rounded bg-transparent hover:bg-white/20 transition"
                                        title="Copy phone number"
                                        aria-label="Copy phone number"
                                    >
                                        <Copy className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                            </>
                        )}



                        {selectedItem.linkedinurl && (
                            <a
                                href={selectedItem.linkedinurl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn flex items-center justify-center gap-2 w-full py-2 px-4 rounded-lg bg-[#0077b5] text-white font-semibold hover:bg-[#005983] transition-colors text-base shadow-md hover:scale-[1.03] active:scale-95"
                            >
                                <Linkedin className="w-5 h-5" />
                                LinkedIn
                            </a>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TeamMemberDialog;
